import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { http } from "../lib/api";
import { Hero } from "../components/home/Hero";
import { DivisionsBento } from "../components/home/DivisionsBento";
import { InventoryPreview } from "../components/home/InventoryPreview";
import { Steps } from "../components/home/Steps";
import { Marquee } from "../components/site/Marquee";
import { TrustSection } from "../components/site/TrustSection";
import { ReviewsGrid } from "../components/site/Reviews";
import { Reveal } from "../components/site/Motion";
import { useSettings } from "../lib/settings";
import { EstablishedSeal } from "../components/site/CompanyStory";
import { HiringBanner } from "../components/careers/HiringBanner";

const MARQUEE = ["Truck Division", "Honest communication", "Trailer Division", "Clear expectations", "Marine Division", "Reliable sourcing", "Equipment Division", "Transparent process"];

export default function Home() {
  const s = useSettings();
  const { data } = useQuery({ queryKey: ["home"], queryFn: async () => (await http.get("/home")).data, retry: 1 });
  const featured = Array.isArray(data?.featured) ? data.featured : [];
  const recent = Array.isArray(data?.recent) ? data.recent : [];
  const reviews = Array.isArray(data?.reviews) ? data.reviews : [];

  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee items={MARQUEE} />
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-4 space-y-8"><p className="eyebrow">Who we are</p><EstablishedSeal testid="home-established-seal" /></Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <p className="font-display text-3xl md:text-5xl leading-[1.12] text-slate-100">
              An asset sourcing and brokerage company that works to understand <em className="text-gold">what you actually need</em> &mdash; then searches the right channels to find it.
            </p>
            <Link to="/about" className="inline-flex items-center gap-2 mt-10 text-sm text-gold hover:gap-4 transition-[gap] duration-300" data-testid="home-about-link">
              About {s.company_name} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
      <DivisionsBento counts={data?.counts} />
      <InventoryPreview key={`${featured.length}-${recent.length}`} featured={featured} recent={recent} />
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <p className="eyebrow">How It Works</p>
              <h2 className="font-display text-5xl md:text-6xl text-slate-50 mt-5 leading-[1]">From request<br /><em>to delivery.</em></h2>
            </Reveal>
            <Reveal delay={0.1}><Link to="/how-it-works" className="btn-ghost" data-testid="home-how-it-works-link">The full process <ArrowRight className="w-4 h-4" /></Link></Reveal>
          </div>
          <Steps />
        </div>
      </section>
      <TrustSection />
      <HiringBanner />
      <section className="py-24 md:py-32" data-testid="home-reviews-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <p className="eyebrow">Client Reviews</p>
              <h2 className="font-display text-5xl md:text-6xl text-slate-50 mt-5 leading-[1]">In their <em>words.</em></h2>
            </Reveal>
            <Reveal delay={0.1}><Link to="/reviews" className="btn-ghost" data-testid="home-all-reviews-link">All reviews <ArrowRight className="w-4 h-4" /></Link></Reveal>
          </div>
          <ReviewsGrid reviews={reviews} />
        </div>
      </section>
      <section className="relative overflow-hidden border-t hairline grain">
        <img src="/img/equipment.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-[#0a0d14]/70" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-28 md:py-36 text-center">
          <Reveal>
            <p className="eyebrow">Can't find it listed?</p>
            <h2 className="font-display text-5xl md:text-7xl text-slate-50 mt-6 leading-[1]">Tell us what you need.<br /><em className="text-gold">We'll start the search.</em></h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/sourcing" className="btn-gold" data-testid="cta-sourcing-button">Request an Asset <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/contact" className="btn-ghost" data-testid="cta-contact-button">Contact Us</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
